import { measureGeneration } from '~/server/static-metrics'
import { GetStaticProps, NextPage } from 'next'
import DefaultErrorPage from 'next/error'
import { ParsedUrlQuery } from 'node:querystring'
import { useState } from 'react'
import { InputGroup } from 'react-bootstrap'
import InfiniteScroll from 'react-infinite-scroller'
import { ArticleList } from 'web_core/assets/article/List'
import { CategorySelector } from 'web_core/assets/category'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESArticle, ESUser } from 'web_core/elastic_search'
import { Profile, fb } from 'web_core/firebase'
import { useQueryData } from 'web_core/hooks'
import { useESArticles, useProfile } from 'web_core/hooks/models'
import {
  getStaticParticipatedTeams,
  getStaticProfile,
} from 'web_core/static_props/profile'
import { Serialized, deserializeArray, isBlank } from 'web_core/utils'
import { getDefaultStaticPaths } from 'web_core/utils/static_paths'
import { addLocaleProps } from 'web_core/utils/static_props'
import { ProfileLayout } from '~/components/Layout/Profile'
import {
  QueryOrder,
  QueryOrderSelector,
} from '~/components/Utils/QueryOrderSelector'
import { isProfileIndexable } from '~/static_props/profile'

interface Params extends ParsedUrlQuery {
  id: string
}

type Props = {
  profile: Serialized<Profile> | null
  articles: Serialized<ESArticle>[]
  teams: Serialized<ESUser>[] | null
  indexable: boolean
}

export const getStaticPaths = getDefaultStaticPaths

export const getStaticProps: GetStaticProps<Props, Params> = async ({
  locale = 'ja',
  params,
}) =>
  measureGeneration('user_articles', async (measure) => {
    if (!params) throw new Error('Missing params.')

    const { data } = await measure(() =>
      fb.call('search')({
        index: 'articles',
        params: {
          type: 'query',
          uids: [params.id],
          apply_recommendation: false,
          order: 'created_at desc',
        },
      })
    )
    if (data.index !== 'articles') {
      throw new Error('Unexpected response.')
    }

    const profile = await measure(() => getStaticProfile(params.id))
    const result = {
      props: {
        profile,
        teams: profile
          ? await measure(() => getStaticParticipatedTeams(profile))
          : null,
        articles: data.result,
        indexable: await measure(() => isProfileIndexable(params.id)),
      },
      revalidate: 10,
    }
    return addLocaleProps(result, locale)
  })

const Page: NextPage<Props> = ({
  profile: staticProfile,
  articles: staticArticles,
  teams,
  indexable,
}) => {
  const id = useQueryData()
  const [categoryId, setCategoryId] = useState<string>()
  const [order, setOrder] = useState<QueryOrder>('created_at desc')
  const { profile, loadingProfile } = useProfile(id, staticProfile)
  const { articles, loadMore, isReachingEnd, isLoadingMore } = useESArticles(
    profile
      ? {
          type: 'query',
          category_ids:
            !isBlank(categoryId) && categoryId !== ''
              ? [categoryId]
              : undefined,
          uids: [profile.id],
          apply_recommendation: false,
          order,
        }
      : null,
    deserializeArray(staticArticles, {})
  )

  if (!isBlank(id) && id !== '' && !profile && !loadingProfile)
    return <DefaultErrorPage statusCode={404} />
  return profile && teams ? (
    <ProfileLayout
      index={indexable}
      title={`${profile.display_name}の記事一覧`}
      description={`${profile.display_name}が投稿した記事を閲覧できます。`}
      activeTab="articles"
      profile={profile}
      teams={deserializeArray(teams, {})}
    >
      <InputGroup className="w-100 mb-3" style={{ maxWidth: 500 }}>
        <CategorySelector
          className="fs-7"
          value={categoryId}
          onChange={setCategoryId}
          undefinedLabel={'全てのカテゴリ'}
        />
        <QueryOrderSelector
          className="fs-7"
          order={order}
          onChange={setOrder}
        />
      </InputGroup>
      <InfiniteScroll
        loadMore={async () => {
          if (
            isReachingEnd === false &&
            isLoadingMore === false &&
            isReachingEnd === false
          )
            await loadMore()
        }}
        hasMore={isReachingEnd === false}
        loader={<LoadingSpinner />}
      >
        {articles && (
          <ArticleList
            showProfileImage={false}
            articles={articles}
            className="grid g-cols-1 g-lg-cols-2"
          />
        )}
      </InfiniteScroll>
    </ProfileLayout>
  ) : (
    <LoadingSpinner />
  )
}

export default Page
